import { useEffect, useState } from 'react';
import { HandCoins } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import upstreamService from '../../services/upstream';
import { money } from '../../utils/nurseryMoney';
import { formatLocalDateInTz } from '../../utils/timezone';
import type { OwedToUs } from '../../types/upstream';

/**
 * What other organisations owe this one under upstream agreements that name
 * it (backend upstreamAgreementService.payeeSummary): trees and amounts per
 * organisation, cultivar, program and month. Aggregates only: the server
 * sends no customer, price or document. Shows nothing when nothing is owed,
 * or when the seat cannot read it.
 */

// A calendar month, not an instant: formatted at noon UTC so no zone moves it.
const monthName = (ym: string) => formatLocalDateInTz(`${ym}-01`, 'UTC', { month: 'short', year: 'numeric' });

export default function OwedToUsCard() {
  const [data, setData] = useState<OwedToUs | null>(null);

  useEffect(() => {
    let cancelled = false;
    upstreamService.owedToUs()
      .then((res) => { if (!cancelled && res.success && res.data) setData(res.data); })
      .catch(() => { /* no seat for it, or nothing to show */ });
    return () => { cancelled = true; };
  }, []);

  if (!data || data.rows.length === 0) return null;
  const t = data.totals;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><HandCoins className="h-5 w-5" /> Owed to you</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-neutral-600 break-words">
          What licensees have recorded as owed to you under their agreements with you: {t.trees_sold} trees sold
          {t.trees_annual ? ` and ${t.trees_annual} licensed tree-years` : ''}, {money(t.amount)} in all, {money(t.paid)} marked paid,
          <strong> {money(t.owed)} still owed</strong>.
        </p>
        <ul className="divide-y divide-neutral-100">
          {data.rows.map((r) => (
            <li key={`${r.owed_by}-${r.cultivar_name}-${r.program_name}-${r.month}`} className="py-1.5 flex flex-wrap justify-between gap-x-3">
              <span className="break-words min-w-0">
                {monthName(r.month)} · {r.owed_by} · {r.cultivar_name || '—'}{r.program_name ? ` (${r.program_name})` : ''}
              </span>
              <span className="text-neutral-700 whitespace-nowrap">
                {r.trees_sold} sold{r.trees_annual ? `, ${r.trees_annual} licensed` : ''} · {money(r.amount)}
                {r.paid >= r.amount && r.amount > 0 ? ' · paid' : r.stated > 0 ? ' · stated' : ''}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
